/**
 * PlayersOverlay - SVG-based block rendering of players in editor mode
 *
 * Renders players using the unified block architecture with:
 * - PlayerVisual for SVG circle rendering
 * - CanvasSelectable + CanvasDraggable behaviors in editor mode
 * - Node linking and group movement
 *
 * Replaces the HTML-based Player.tsx layer for WYSIWYG consistency.
 */

import { useMemo } from 'react'
import { BlockRenderProvider } from '@/components/blocks/BlockRenderContext'
import { CanvasBlockRenderer } from '@/components/blocks/CanvasBlockRenderer'
import { playerToPlayerBlock } from '@/adapters/PlayerAdapter'
import { createPixelScaleFactors } from '@/utils/RenderScale'
import { constrainToField, snapToGrid } from '@/utils/GridSnap.utils'
import type { BlockRenderContextValue } from '@/components/blocks/Types'
import type { Player } from '@/types'
import type { Drawing } from '@/types/Drawing.types'
import type { Tool } from '@/types/Play.types'
import type { FieldCoordinateSystem } from '@/utils/Coordinates'

interface PlayersOverlayProps {
	players: Player[]
	viewBox: { width: number; height: number }
	coordSystem?: FieldCoordinateSystem
	drawings?: Drawing[]
	currentTool?: Tool
	selectedIds?: Set<string>
	onMove?: (id: string, x: number, y: number) => void
	onSelect?: (id: string, additive: boolean) => void
	onDelete?: (id: string) => void
	onFill?: (id: string) => void
	onLabelClick?: (id: string, x: number, y: number) => void
	onLinkToNode?: (playerId: string, drawingId: string, pointId: string) => void
	onHoverChange?: (id: string, isHovered: boolean) => void
	getGroupedBlockIds?: (blockId: string) => string[]
	zoom?: number
	panX?: number
	panY?: number
}

/**
 * SVG-based players overlay using unified block architecture.
 *
 * Renders inside parent SVG element. Provides BlockRenderContext
 * for editor behaviors (select, drag, delete, fill).
 */
export function PlayersOverlay({
	players,
	viewBox,
	coordSystem,
	drawings = [],
	currentTool = 'select',
	selectedIds,
	onMove,
	onSelect,
	onDelete,
	onFill,
	onLabelClick,
	onLinkToNode,
	onHoverChange,
	getGroupedBlockIds,
	zoom = 1,
	panX = 0,
	panY = 0,
}: PlayersOverlayProps) {
	// Convert players to blocks
	const playerBlocks = useMemo(
		() => players.map(playerToPlayerBlock),
		[players]
	)

	// Create coordinate transform: feet → pixels (includes Y-flip)
	// If coordSystem is provided, use it for accurate pixel positioning
	// Otherwise fall back to simple Y-flip for viewBox-based rendering
	const coordinateTransform = useMemo(
		() => (point: { x: number; y: number }) => {
			if (coordSystem) {
				// Convert feet to pixels using the field coordinate system
				return coordSystem.feetToPixels(point.x, point.y)
			}
			// Fallback: simple Y-flip for feet-based viewBox
			return {
				x: point.x,
				y: viewBox.height - point.y,
			}
		},
		[coordSystem, viewBox.height]
	)

	// Convert delta to absolute position, enforcing LOS exclusion and sideline bounds
	const handleMove = useMemo(() => {
		if (!onMove) return undefined
		return (id: string, delta: { x: number; y: number }) => {
			const player = players.find(p => p.id === id)
			if (player) {
				const constrained = constrainToField(player.x + delta.x, player.y + delta.y)
				onMove(id, constrained.x, constrained.y)
			}
		}
	}, [onMove, players])

	// Snap to grid on drag end (enforces LOS exclusion zone and grid alignment)
	const handleDragEnd = useMemo(() => {
		if (!onMove) return undefined
		return (id: string) => {
			const player = players.find(p => p.id === id)
			if (player) {
				const snapped = snapToGrid(player.x, player.y)
				if (snapped.x !== player.x || snapped.y !== player.y) {
					onMove(id, snapped.x, snapped.y)
				}
			}
		}
	}, [onMove, players])

	// Calculate scale factors for pixel-based rendering
	// When using coordSystem (pixel mode), scale player sizes to pixels
	const scale = useMemo(
		() => coordSystem ? createPixelScaleFactors(coordSystem) : undefined,
		[coordSystem],
	)

	// Build context value for block rendering
	const contextValue: BlockRenderContextValue = useMemo(
		() => ({
			mode: 'editor',
			zoom,
			coordinateTransform,
			viewBox,
			scale,
			selectedIds,
			coordSystem,
			drawings,
			currentTool,
			panX,
			panY,
			onMove: handleMove,
			onDragEnd: handleDragEnd,
			onSelect,
			onDelete,
			onFill,
			onLabelClick,
			onLinkToNode,
			onHoverChange,
			getGroupedBlockIds,
		}),
		[
			zoom,
			coordinateTransform,
			viewBox,
			scale,
			selectedIds,
			coordSystem,
			drawings,
			currentTool,
			panX,
			panY,
			handleMove,
			handleDragEnd,
			onSelect,
			onDelete,
			onFill,
			onLabelClick,
			onLinkToNode,
			onHoverChange,
			getGroupedBlockIds,
		]
	)

	return (
		<BlockRenderProvider value={contextValue}>
			<g className="players-overlay">
				{playerBlocks.map(block => (
					<CanvasBlockRenderer key={block.id} block={block} />
				))}
			</g>
		</BlockRenderProvider>
	)
}
